import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Search, PlusCircle, Edit, Trash2, ExternalLink, Loader2, Filter } from "lucide-react";

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("status", statusFilter);
      if (q.trim()) params.append("q", q.trim());
      const { data } = await api.get(`/admin/articles?${params.toString()}`);
      setArticles(data);
    } catch (e) {
      toast.error("Error al cargar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [statusFilter]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`¿Eliminar "${title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success("Artículo eliminado");
      setArticles((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Artículos</h1>
          <p className="text-slate-600 mt-1">Gestiona todo el contenido del blog</p>
        </div>
        <Link to="/admin/articulos/nuevo" className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-teal-600 text-white rounded-xl shadow-md hover:shadow-lg transition font-medium">
          <PlusCircle size={18} /> Nuevo artículo
        </Link>
      </div>

      <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex gap-3 flex-wrap">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 min-w-[220px] relative">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por título..."
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </form>
        <div className="relative">
          <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-9 pr-8 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white appearance-none"
          >
            <option value="all">Todos</option>
            <option value="published">Publicados</option>
            <option value="draft">Borradores</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="animate-spin mx-auto text-cyan-600" /></div>
        ) : articles.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <p>No se encontraron artículos.</p>
            <Link to="/admin/articulos/nuevo" className="inline-block mt-3 text-cyan-700 font-medium hover:underline">Crear uno nuevo</Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {articles.map((a) => (
              <div key={a.id} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition">
                {a.featured_image ? (
                  <img src={a.featured_image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-slate-100 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{a.title}</div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="bg-slate-100 px-2 py-0.5 rounded">{a.category}</span>
                    <StatusBadge status={a.status} />
                    <span>Actualizado {new Date(a.updated_at).toLocaleDateString("es-MX")}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {a.status === "published" && (
                    <a href={`/articulos/${a.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-500 hover:text-cyan-700 rounded-lg hover:bg-slate-100 transition" title="Ver público">
                      <ExternalLink size={18} />
                    </a>
                  )}
                  <Link to={`/admin/articulos/${a.id}/editar`} className="p-2 text-slate-500 hover:text-cyan-700 rounded-lg hover:bg-slate-100 transition" title="Editar">
                    <Edit size={18} />
                  </Link>
                  <button onClick={() => handleDelete(a.id, a.title)} className="p-2 text-slate-500 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition" title="Eliminar">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    published: { txt: "Publicado", cls: "bg-emerald-100 text-emerald-700" },
    draft: { txt: "Borrador", cls: "bg-amber-100 text-amber-700" },
  };
  const it = map[status] || { txt: status, cls: "bg-slate-100 text-slate-700" };
  return <span className={`${it.cls} text-xs px-2 py-0.5 rounded-full font-medium`}>{it.txt}</span>;
}
